import { Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gray-900 dark:bg-black text-white py-16 border-t border-gray-200 dark:border-white/10">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Logo et description */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2">
              <div className="relative group">
                <img 
                  src="/Fab43-Logo-lineaire.svg" 
                  alt="Fab43" 
                  className="h-64 w-64 text-white transition-all duration-300 relative z-10"
                />
                <div className="absolute inset-0 bg-primary-500/20 dark:bg-white/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0">
                  {/* SHOCKING electricity effect on hover */}
                  <div className="absolute top-0 left-0 w-full h-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {/* Lightning bolts */}
                    <div className="absolute top-1/4 left-1/4 w-1 h-8 bg-gradient-to-b from-yellow-400 via-orange-400 to-red-500 transform rotate-45 animate-pulse opacity-0 group-hover:opacity-100 transition-opacity duration-100"></div>
                    <div className="absolute top-1/3 right-1/4 w-1 h-6 bg-gradient-to-b from-yellow-400 via-orange-400 to-red-500 transform -rotate-12 animate-pulse opacity-0 group-hover:opacity-100 transition-opacity duration-150"></div>
                    <div className="absolute bottom-1/4 left-1/3 w-1 h-10 bg-gradient-to-b from-yellow-400 via-orange-400 to-red-500 transform rotate-12 animate-pulse opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
                    <div className="absolute top-1/2 right-1/3 w-1 h-7 bg-gradient-to-b from-yellow-400 via-orange-400 to-red-500 transform -rotate-45 animate-pulse opacity-0 group-hover:opacity-100 transition-opacity duration-250"></div>
                    
                    {/* Electric arcs */}
                    <div className="absolute top-0 left-0 w-full h-full">
                      <div className="absolute top-1/4 left-1/4 w-16 h-16 border-2 border-yellow-400 rounded-full opacity-0 group-hover:opacity-60 animate-ping"></div>
                      <div className="absolute top-1/2 right-1/4 w-12 h-12 border-2 border-orange-400 rounded-full opacity-0 group-hover:opacity-40 animate-ping" style={{animationDelay: '0.1s'}}></div>
                      <div className="absolute bottom-1/3 left-1/3 w-8 h-8 border-2 border-red-500 rounded-full opacity-0 group-hover:opacity-30 animate-ping" style={{animationDelay: '0.2s'}}></div>
                    </div>
                    
                    {/* Electric glow */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-yellow-400/20 via-orange-400/20 to-red-500/20 opacity-0 group-hover:opacity-100 animate-pulse"></div>
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-yellow-400/10 via-orange-400/10 to-red-500/10 opacity-0 group-hover:opacity-100 animate-pulse" style={{animationDelay: '0.5s'}}></div>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-gray-300 dark:text-white/70 leading-relaxed mb-6 max-w-md">
              L'espace hybride où la créativité rencontre la technologie. 
              FabLab de pointe et centre de formation pour tous les innovateurs 
              de demain.
            </p>
            <div className="space-y-2 text-sm text-gray-300 dark:text-white/60">
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4" />
                <a 
                  href="https://share.google/hX2tMH6VRJTADaSyc" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 dark:text-white/60 hover:text-primary-400 dark:hover:text-primary-300 transition-colors duration-200"
                >
                  CYBER PARK, Rue 18 Janvier 1952, Kasserine 1200
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="h-4 w-4" />
                <a 
                  href="tel:+21656043040" 
                  className="text-gray-300 dark:text-white/60 hover:text-primary-400 dark:hover:text-primary-300 transition-colors duration-200"
                >
                  +216 56 043 040
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4" />
                <a 
                  href="mailto:contact@fab43.com" 
                  className="text-gray-300 dark:text-white/60 hover:text-primary-400 dark:hover:text-primary-300 transition-colors duration-200"
                >
                  contact@fab43.com
                </a>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Navigation</h3>
            <ul className="space-y-3">
              {[
                { name: 'Accueil', id: 'hero' },
                { name: 'À Propos', id: 'about' },
                { name: 'Services', id: 'services' },
                { name: 'Public Cible', id: 'target' }
              ].map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => scrollToSection(item.id)}
                    className="text-gray-300 dark:text-white/70 hover:text-gray-900 dark:hover:text-white transition-all duration-300 text-sm relative group"
                  >
                    {item.name}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gray-900 dark:bg-white transition-all duration-300 group-hover:w-full"></span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Nos Services</h3>
            <ul className="space-y-3 text-sm text-gray-300 dark:text-white/70">
              <li>Support de Projet</li>
              <li>Espace de Travail</li>
              <li>Formations & Ateliers</li>
              <li>Bootcamps</li>
              <li>Impression 3D</li>
              <li>Découpe Laser</li>
              <li>Robotique & IoT</li>
            </ul>
          </div>
        </div>

        {/* Newsletter */}
        <div className="border-t border-gray-200 dark:border-white/10 pt-12 mb-12">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-2xl font-bold mb-4">Restez connecté</h3>
            <p className="text-gray-600 dark:text-white/70 mb-6">
              Recevez nos actualités, événements et opportunités directement dans votre boîte mail.
            </p>
            <div className="flex flex-col sm:flex-row max-w-md mx-auto">
              <input
                type="email"
                placeholder="Votre adresse email"
                className="flex-1 px-4 py-3 bg-white dark:bg-white/10 border border-gray-300 dark:border-white/20 rounded-l-lg sm:rounded-r-none rounded-r-lg focus:ring-2 focus:ring-primary-500 dark:focus:ring-white/50 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-white/50"
              />
              <button className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-r-lg sm:rounded-l-none rounded-l-lg transition-all duration-300 font-semibold mt-2 sm:mt-0">
                S'abonner
              </button>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-200 dark:border-white/10 pt-8 text-center">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 dark:text-white/60 text-sm mb-4 md:mb-0">
              © {new Date().getFullYear()} Fab43. Tous droits réservés. Fondé par <a href="https://www.linkedin.com/in/ahmed-missaoui-499ab3145" target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:underline">Ahmed Missaoui</a>. Développé par <a href="https://holmena.com" target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:underline">HOLMENA</a>
            </p>
            {/*
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500 dark:text-white/60">
              <a href="#" className="hover:text-gray-900 dark:hover:text-white transition-all duration-300">Mentions légales</a>
              <a href="#" className="hover:text-gray-900 dark:hover:text-white transition-all duration-300">Politique de confidentialité</a>
              <a href="#" className="hover:text-gray-900 dark:hover:text-white transition-all duration-300">CGU</a>
            </div> */}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;